<template>
  <b-container class="mt-5">
    <b-row>
      <b-col v-bind="authColProps" class="mx-auto auth-card text-center">
        <b-overlay :show="verifying" rounded="sm">
          <Logo :logoProps="logo" class="mb-4" />

          <div v-if="linkInvalid" class="link-expired">
            <p class="mb-4">{{ errorMsg }}</p>
            <router-link to="/" class="link-expired-back">Back to login</router-link>
          </div>

          <div v-else-if="linkExpired" class="link-expired">
            <h5 class="mb-3">This link has expired</h5>
            <p class="mb-4">
              For security, password setup links are only valid for {{ expirationMinutes }} minutes. Click below to get a new link sent to your inbox.
            </p>
            <b-button
              variant="primary"
              class="btn-resend"
              :disabled="resending"
              @click="resendEmail"
            >
              <b-spinner small v-if="resending" label="Loading..."></b-spinner>
              <span v-else>Resend email</span>
            </b-button>
          </div>

          <div v-else-if="!verifying">
            <h4 class="mt-4 mb-4">Create Password</h4>

            <b-form
              @submit.stop.prevent="onSubmit"
              class="forms text-left"
            >
              <b-form-group class="mb-2">
                <b-form-input
                  type="password"
                  v-model="$v.form.newPassword.$model"
                  placeholder="New Password"
                  :state="$v.form.newPassword.$dirty ? !$v.form.newPassword.$error : null"
                />
              </b-form-group>

              <ul class="password-rules list-unstyled">
                <li :class="{ 'rule-met': passwordRules.minLength }">
                  <span class="rule-icon">{{ passwordRules.minLength ? "✓" : "✗" }}</span>
                  Minimum 10 characters
                </li>
                <li :class="{ 'rule-met': passwordRules.upper }">
                  <span class="rule-icon">{{ passwordRules.upper ? "✓" : "✗" }}</span>
                  At least one uppercase letter
                </li>
                <li :class="{ 'rule-met': passwordRules.lower }">
                  <span class="rule-icon">{{ passwordRules.lower ? "✓" : "✗" }}</span>
                  At least one lowercase letter
                </li>
                <li :class="{ 'rule-met': passwordRules.digit }">
                  <span class="rule-icon">{{ passwordRules.digit ? "✓" : "✗" }}</span>
                  At least one number
                </li>
                <li :class="{ 'rule-met': passwordRules.special }">
                  <span class="rule-icon">{{ passwordRules.special ? "✓" : "✗" }}</span>
                  At least one special character
                </li>
              </ul>

              <b-form-group class="mb-4">
                <b-form-input
                  type="password"
                  v-model="$v.form.confirmNewPassword.$model"
                  placeholder="Confirm Password"
                  @keyup.enter="onSubmit()"
                  :state="$v.form.confirmNewPassword.$dirty ? !$v.form.confirmNewPassword.$error : null"
                />
                <b-form-invalid-feedback>Passwords must be identical.</b-form-invalid-feedback>
              </b-form-group>

              <b-button
                type="submit"
                variant="primary"
                block
                :disabled="submitted || $v.form.$invalid"
              >
                <b-spinner small v-if="submitted" label="Loading..."></b-spinner>
                <span v-else>Submit</span>
              </b-button>
            </b-form>

            <router-link to="/" class="d-inline-block mt-4">Back to login</router-link>
          </div>
        </b-overlay>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { HTTP } from "../../utils/HTTP";
import { clearLocalSession } from "../../utils/auth-requests";
import { Helper } from "../../helpers";
import { validationMixin } from "vuelidate";
import { required, minLength, sameAs, helpers } from "vuelidate/lib/validators";
import Logo from "../../components/logo/logo.vue";

// EB2-58207: strong-password policy — min 10 characters, at least one
// uppercase, one lowercase, one digit, and one special character.
const hasUpperCase = helpers.regex( "hasUpperCase", /[A-Z]/ );
const hasLowerCase = helpers.regex( "hasLowerCase", /[a-z]/ );
const hasDigit = helpers.regex( "hasDigit", /[0-9]/ );
const hasSpecialChar = helpers.regex( "hasSpecialChar", /[^A-Za-z0-9]/ );

const LINK_EXPIRED_MESSAGE = "The link used has expired, please try a valid link.";

export default {
  name: "CreatePassword",
  mixins: [validationMixin],
  components: {
    Logo,
  },
  data () {
    return {
      logo: {
        width: "auto",
        height: "50px",
        class: "mt-2",
      },
      token: null,
      userId: null,
      verifying: true,
      linkInvalid: false,
      linkExpired: false,
      // Fallback shown only if the backend response omits expiration_minutes.
      expirationMinutes: 60,
      errorMsg: null,
      submitted: false,
      resending: false,
      form: {
        newPassword: null,
        confirmNewPassword: null,
      },
    };
  },
  validations: {
    form: {
      newPassword: {
        required,
        minLength: minLength( 10 ),
        hasUpperCase,
        hasLowerCase,
        hasDigit,
        hasSpecialChar,
      },
      confirmNewPassword: {
        required,
        sameAsPassword: sameAs( "newPassword" ),
      },
    },
  },
  computed: {
    authColProps () {
      if ( this.linkInvalid || this.linkExpired ) {
        return { cols: "12" };
      }
      return { cols: "12", md: "7", lg: "5", xl: "4" };
    },
    passwordRules () {
      let pwd = this.form.newPassword || "";
      return {
        minLength: pwd.length >= 10,
        upper: /[A-Z]/.test( pwd ),
        lower: /[a-z]/.test( pwd ),
        digit: /[0-9]/.test( pwd ),
        special: /[^A-Za-z0-9]/.test( pwd ),
      };
    },
  },
  mounted () {
    this.token = this.$route.params.token;
    this.verifyMagicLink();
  },
  methods: {
    async verifyMagicLink () {
      if ( !this.token ) {
        this.verifying = false;
        this.linkInvalid = true;
        this.errorMsg = LINK_EXPIRED_MESSAGE;
        return;
      }
      try {
        
        let response = await HTTP( "post", "verify-magic-link", { token: this.token } );
        let data = response && response.data;
        if ( data && data.status ) {
          this.userId = data.user_id;
        } else if ( data && data.errorKey === "TokenExpired" ) {
          this.linkExpired = true;
          if ( data.expiration_minutes ) {
            this.expirationMinutes = data.expiration_minutes;
          }
        } else {
          this.linkInvalid = true;
          this.errorMsg = LINK_EXPIRED_MESSAGE;
        }
      } catch ( error ) {
        this.linkInvalid = true;
        this.errorMsg = "Please Try Again Later.";
        console.log( "verifyMagicLink Error", error );
      } finally {
        this.verifying = false;
      }
    },
    async resendEmail () {
      this.resending = true;
      try {
        
        let response = await HTTP( "post", "resend-magic-link", { token: this.token } );
        let data = response && response.data;
        if ( data && data.status && data.action === "show_password" ) {
          this.$router.push( {
            name: "login",
            path: "/",
            params: { successMsg: "Your password is already set. Please log in." },
          } );
        } else if ( data && data.status ) {
          Helper.makeToast( this.$bvToast, "success", data.message || "We have emailed you a link to set up your password." );
        } else {
          this.linkExpired = false;
          this.linkInvalid = true;
          this.errorMsg = LINK_EXPIRED_MESSAGE;
        }
      } catch ( error ) {
        Helper.makeToast( this.$bvToast, "danger", "Please Try Again Later" );
        console.log( "resendEmail Error", error );
      } finally {
        this.resending = false;
      }
    },
    onSubmit () {
      this.$v.form.$touch();
      if ( this.$v.form.$anyError ) {
        return;
      }
      this.createPassword();
    },
    async createPassword () {
      this.submitted = true;
      try {
        let response = await HTTP( "post", "set-password", {
          user_id: this.userId,
          token: this.token,
          password: this.form.newPassword,
          password_confirmation: this.form.confirmNewPassword,
        } );
        let data = response && response.data;
        if ( data && data.status ) {
          // Setting the password ends every existing session. Drop any stale one in this browser,
          // otherwise the router sends the login page straight on to the dashboard
          if ( localStorage.getItem( "accessToken" ) ) {
            clearLocalSession( this.$store );
          }
          this.$router.push( {
            name: "login",
            path: "/",
            params: { successMsg: data.message || "Password created. Please log in." },
          } );
        } else {
          this.linkInvalid = true;
          this.errorMsg = LINK_EXPIRED_MESSAGE;
        }
      } catch ( error ) {
        Helper.makeToast( this.$bvToast, "danger", "Please Try Again Later" );
        console.log( "createPassword Error", error );
      } finally {
        this.submitted = false;
      }
    },
  },
};
</script>

<style scoped>
.auth-card {
  float: none;
}
.link-expired-back {
  color: #495057;
  text-decoration: underline;
}
.link-expired-back:hover {
  color: #212529;
}
.link-expired p {
  font-size: 0.95rem;
}
.btn-resend {
  border-radius: 50rem;
  padding: 0.6rem 2rem;
}
.password-rules {
  font-size: 0.85rem;
  color: #8a94a6;
  margin: 0.5rem 0 1.25rem;
}
.password-rules li {
  margin-bottom: 4px;
}
.password-rules .rule-icon {
  display: inline-block;
  width: 1.2em;
}
.password-rules li.rule-met {
  color: #2e7d4f;
}
</style>
